import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { CATEGORIES } from '../constants/inventory';
import { resizeImageToDataUrl } from '../utils/image';
import InventoryScanReview, { CandidateItem } from './InventoryScanReview';
import Icon from './Icon';
import Toast from './Toast';
import UnitSelect from './UnitSelect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

interface InventoryItem {
  id: number;
  name: string;
  quantity: number;
  unit: string;
  category: string;
  freshness_score: number;
  expiration_date: string | null;
}

interface NewItem {
  name: string;
  quantity: string;
  unit: string;
  category: string;
  expiration_date: string;
}

const emptyNewItem: NewItem = { name: '', quantity: '', unit: '', category: '', expiration_date: '' };

const Pantry: React.FC = () => {
  const queryClient = useQueryClient();
  const [showAddChooser, setShowAddChooser] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [scanReviewTitle, setScanReviewTitle] = useState('');
  const [scanCandidates, setScanCandidates] = useState<CandidateItem[] | null>(null);
  // InventoryScanReview seeds its editable rows from props only on mount, so
  // swapping in a different candidate list (e.g. narrowing down to just the
  // items that failed to save) needs a fresh instance to pick it up.
  const [scanReviewKey, setScanReviewKey] = useState(0);

  const receiptInputRef = useRef<HTMLInputElement>(null);
  const itemInputRef = useRef<HTMLInputElement>(null);

  const [newItem, setNewItem] = useState<NewItem>(emptyNewItem);

  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [editForm, setEditForm] = useState<NewItem>(emptyNewItem);

  const { data: items, isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/inventory/`);
      return response.data as InventoryItem[];
    }
  });

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const addItemMutation = useMutation({
    mutationFn: async (item: NewItem) => {
      const response = await axios.post(`${API_URL}/inventory/add/`, item);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowAddModal(false);
      setNewItem(emptyNewItem);
      setVoiceTranscript('');
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error adding item. Please try again.' });
    }
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`${API_URL}/inventory/${id}/delete/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error deleting item.' });
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, item }: { id: number; item: NewItem }) => {
      const response = await axios.patch(`${API_URL}/inventory/${id}/update/`, item);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setEditingItem(null);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error updating item. Please try again.' });
    }
  });

  const scanReceiptMutation = useMutation({
    mutationFn: async (dataUrl: string) => {
      const response = await axios.post(`${API_URL}/inventory/scan-receipt/`, { image: dataUrl });
      return response.data.items as CandidateItem[];
    },
    onSuccess: (candidateItems) => {
      setScanReviewTitle('Review Receipt Items');
      setScanCandidates(candidateItems);
      setScanReviewKey((k) => k + 1);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Could not scan the receipt. Please try again.' });
    }
  });

  const scanItemMutation = useMutation({
    mutationFn: async (dataUrl: string) => {
      const response = await axios.post(`${API_URL}/inventory/scan-item/`, { image: dataUrl });
      return response.data.items as CandidateItem[];
    },
    onSuccess: (candidateItems) => {
      setScanReviewTitle('Review Detected Item');
      setScanCandidates(candidateItems);
      setScanReviewKey((k) => k + 1);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Could not identify the item. Please try again.' });
    }
  });

  const confirmScanMutation = useMutation({
    mutationFn: async (confirmedItems: CandidateItem[]) => {
      // allSettled, not all -- these fire as concurrent requests from a
      // phone that's often on spotty wifi/cellular mid-grocery-run, so one
      // dropped connection shouldn't sink the whole batch and silently
      // discard items that actually did save.
      const results = await Promise.allSettled(
        confirmedItems.map((item) => axios.post(`${API_URL}/inventory/add/`, item))
      );
      const failed = results
        .map((result, i) => ({ result, item: confirmedItems[i] }))
        .filter((r): r is { result: PromiseRejectedResult; item: CandidateItem } => r.result.status === 'rejected');
      return {
        succeededCount: confirmedItems.length - failed.length,
        failedItems: failed.map((f) => f.item),
        firstError: failed[0]?.result.reason,
      };
    },
    onSuccess: ({ succeededCount, failedItems, firstError }) => {
      if (succeededCount > 0) {
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      }

      if (failedItems.length === 0) {
        setScanCandidates(null);
        setMessage({ type: 'success', text: `Added ${succeededCount} item${succeededCount === 1 ? '' : 's'} to pantry.` });
        return;
      }

      // Keep the review modal open with just the items that didn't save --
      // everything the user already scanned/typed for them is preserved,
      // no need to rescan or retype to retry.
      setScanReviewTitle(failedItems.length === 1 ? 'Retry Failed Item' : 'Retry Failed Items');
      setScanCandidates(failedItems);
      setScanReviewKey((k) => k + 1);

      const detail = errorDetail(firstError);
      const savedNote = succeededCount > 0 ? `Added ${succeededCount}. ` : '';
      setMessage({
        type: 'error',
        text: `${savedNote}${failedItems.length} item${failedItems.length === 1 ? '' : 's'} failed to add`
          + (detail ? `: ${detail}` : '.') + ' Review and try again below.',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newItem.name && newItem.quantity && newItem.unit) {
      addItemMutation.mutate(newItem);
    }
  };

  const handleDelete = (item: InventoryItem) => {
    if (window.confirm(`Delete ${item.name} from pantry?`)) {
      deleteItemMutation.mutate(item.id);
    }
  };

  const openEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setEditForm({
      name: item.name,
      quantity: String(item.quantity),
      unit: item.unit,
      category: item.category,
      expiration_date: item.expiration_date || '',
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem && editForm.name && editForm.quantity && editForm.unit) {
      updateItemMutation.mutate({ id: editingItem.id, item: editForm });
    }
  };

  const openChooser = () => {
    setMessage(null);
    setShowAddChooser(true);
  };

  const chooseManual = () => {
    setShowAddChooser(false);
    setShowAddModal(true);
  };

  const chooseReceiptScan = () => {
    setShowAddChooser(false);
    receiptInputRef.current?.click();
  };

  const chooseItemScan = () => {
    setShowAddChooser(false);
    itemInputRef.current?.click();
  };

  const handleReceiptFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      scanReceiptMutation.mutate(dataUrl);
    } catch {
      setMessage({ type: 'error', text: 'Could not process that photo. Please try again.' });
    }
  };

  const handleItemFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      scanItemMutation.mutate(dataUrl);
    } catch {
      setMessage({ type: 'error', text: 'Could not process that photo. Please try again.' });
    }
  };

  const parseVoiceInput = (transcript: string) => {
    const text = transcript.toLowerCase().trim();

    // Remove "add" or "get" from beginning if present
    let cleanText = text.replace(/^(add|get|buy|purchase)\s+/i, '');

    // Convert word numbers to digits
    cleanText = convertWordsToNumbers(cleanText);

    // Try multiple parsing patterns
    let parsed = parseWithUnit(cleanText) || parseWithoutUnit(cleanText);

    if (parsed) {
      setNewItem({
        name: parsed.name,
        quantity: parsed.quantity,
        unit: parsed.unit,
        category: parsed.category,
        expiration_date: ''
      });
    } else {
      // Fallback: just set the name
      setNewItem(prev => ({
        ...prev,
        name: cleanText
      }));
    }
  };

  const convertWordsToNumbers = (text: string): string => {
    const numberWords: Record<string, string> = {
      'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4',
      'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9',
      'ten': '10', 'eleven': '11', 'twelve': '12', 'thirteen': '13',
      'fourteen': '14', 'fifteen': '15', 'sixteen': '16', 'seventeen': '17',
      'eighteen': '18', 'nineteen': '19', 'twenty': '20', 'thirty': '30',
      'forty': '40', 'fifty': '50', 'a': '1', 'an': '1', 'couple': '2',
      'few': '3', 'several': '5', 'half': '0.5', 'quarter': '0.25'
    };

    let result = text;
    for (const [word, number] of Object.entries(numberWords)) {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      result = result.replace(regex, number);
    }
    return result;
  };

  const parseWithUnit = (text: string): { name: string; quantity: string; unit: string; category: string } | null => {
    // Try multiple patterns in order of specificity

    // Pattern 1: "2 pounds of chicken breast" or "3 cans of beans"
    let match = text.match(/^(\d+(?:\.\d+)?)\s+([a-z]+)\s+of\s+(.+)$/i);
    if (match) {
      const [, quantity, unit, name] = match;
      const normalizedUnit = normalizeUnit(unit);
      const cleanName = name.trim();
      const category = detectCategory(cleanName);

      return {
        name: cleanName,
        quantity,
        unit: normalizedUnit,
        category
      };
    }

    // Pattern 2: "2 pounds chicken breast" (no "of")
    match = text.match(/^(\d+(?:\.\d+)?)\s+([a-z]+)\s+(.+)$/i);
    if (match) {
      const [, quantity, unit, name] = match;

      // Make sure the "unit" is actually a unit word, not part of the name
      const normalizedUnit = normalizeUnit(unit);

      // Check if it's a known unit
      if (isKnownUnit(unit)) {
        const cleanName = name.trim();
        const category = detectCategory(cleanName);

        return {
          name: cleanName,
          quantity,
          unit: normalizedUnit,
          category
        };
      }
    }

    return null;
  };

  const parseWithoutUnit = (text: string): { name: string; quantity: string; unit: string; category: string } | null => {
    // Pattern: "2 avocados" or "5 apples" or "dozen eggs"
    const match = text.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);

    if (match) {
      const [, quantity, name] = match;
      let cleanName = name.trim();

      // Remove plural 's' or 'es' from the end
      if (cleanName.endsWith('es')) {
        cleanName = cleanName.slice(0, -2);
        // Handle special cases like "tomatoes" -> "tomato"
        if (!cleanName.endsWith('o')) {
          cleanName = cleanName + 'e';
        }
      } else if (cleanName.endsWith('s') && !cleanName.endsWith('ss')) {
        cleanName = cleanName.slice(0, -1);
      }

      const category = detectCategory(cleanName);

      return {
        name: cleanName,
        quantity,
        unit: 'count',
        category
      };
    }
    return null;
  };

  const isKnownUnit = (unit: string): boolean => {
    const knownUnits = [
      // Weight
      'pound', 'pounds', 'lb', 'lbs', 'ounce', 'ounces', 'oz',
      'kilogram', 'kilograms', 'kg', 'gram', 'grams', 'g',
      // Volume
      'cup', 'cups', 'tablespoon', 'tablespoons', 'tbsp',
      'teaspoon', 'teaspoons', 'tsp', 'gallon', 'gallons',
      'liter', 'liters', 'l', 'quart', 'quarts', 'pint', 'pints',
      // Count/Containers
      'bottle', 'bottles', 'jar', 'jars', 'can', 'cans',
      'container', 'containers', 'box', 'boxes', 'bag', 'bags',
      'bunch', 'bunches', 'head', 'heads', 'clove', 'cloves',
      'piece', 'pieces', 'count', 'item', 'items', 'package', 'packages'
    ];

    return knownUnits.includes(unit.toLowerCase());
  };

  const normalizeUnit = (spokenUnit: string): string => {
    const unitMap: Record<string, string> = {
      // Weight
      'pound': 'lbs',
      'pounds': 'lbs',
      'lb': 'lbs',
      'lbs': 'lbs',
      'ounce': 'oz',
      'ounces': 'oz',
      'oz': 'oz',
      'kilogram': 'kg',
      'kilograms': 'kg',
      'kg': 'kg',
      'gram': 'g',
      'grams': 'g',
      'g': 'g',

      // Volume
      'cup': 'cup',
      'cups': 'cup',
      'tablespoon': 'tbsp',
      'tablespoons': 'tbsp',
      'tbsp': 'tbsp',
      'teaspoon': 'tsp',
      'teaspoons': 'tsp',
      'tsp': 'tsp',
      'gallon': 'gallon',
      'gallons': 'gallon',
      'liter': 'liter',
      'liters': 'liter',
      'l': 'liter',

      // Count
      'piece': 'count',
      'pieces': 'count',
      'count': 'count',
      'item': 'count',
      'items': 'count',

      // Containers
      'bottle': 'bottle',
      'bottles': 'bottle',
      'jar': 'jar',
      'jars': 'jar',
      'can': 'jar',
      'cans': 'jar',
      'container': 'container',
      'containers': 'container',
      'box': 'container',
      'boxes': 'container',
      'bag': 'container',
      'bags': 'container',

      // Special
      'bunch': 'bunch',
      'bunches': 'bunch',
      'head': 'count',
      'heads': 'count',
      'clove': 'count',
      'cloves': 'count',
    };

    return unitMap[spokenUnit.toLowerCase()] || spokenUnit;
  };

  const detectCategory = (itemName: string): string => {
    const name = itemName.toLowerCase();

    // Protein keywords
    const proteinKeywords = ['chicken', 'beef', 'pork', 'fish', 'salmon', 'tuna', 'turkey',
      'lamb', 'shrimp', 'steak', 'ground beef', 'bacon', 'sausage', 'ham', 'meat', 'egg'];
    if (proteinKeywords.some(k => name.includes(k))) return 'Protein';

    // Dairy keywords
    const dairyKeywords = ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'sour cream',
      'cottage cheese', 'mozzarella', 'cheddar', 'parmesan', 'feta'];
    if (dairyKeywords.some(k => name.includes(k))) return 'Dairy';

    // Grains keywords
    const grainsKeywords = ['rice', 'pasta', 'bread', 'flour', 'oats', 'quinoa', 'barley',
      'cereal', 'noodles', 'spaghetti', 'couscous', 'wheat'];
    if (grainsKeywords.some(k => name.includes(k))) return 'Grains';

    // Vegetables keywords
    const vegetableKeywords = ['onion', 'garlic', 'tomato', 'carrot', 'celery', 'potato',
      'broccoli', 'spinach', 'lettuce', 'pepper', 'cucumber', 'zucchini', 'mushroom',
      'cabbage', 'cauliflower', 'bean', 'peas', 'corn', 'kale', 'arugula'];
    if (vegetableKeywords.some(k => name.includes(k))) return 'Vegetables';

    // Fruits keywords
    const fruitKeywords = ['apple', 'banana', 'orange', 'lemon', 'lime', 'berry', 'strawberry',
      'blueberry', 'grape', 'mango', 'pineapple', 'peach', 'pear', 'melon', 'watermelon',
      'cherry', 'plum', 'kiwi', 'avocado'];
    if (fruitKeywords.some(k => name.includes(k))) return 'Fruits';

    // Baking keywords
    const bakingKeywords = ['sugar', 'salt', 'baking powder', 'baking soda', 'yeast',
      'vanilla', 'cocoa', 'chocolate chips', 'honey', 'syrup'];
    if (bakingKeywords.some(k => name.includes(k))) return 'Baking';

    // Oils keywords
    const oilKeywords = ['oil', 'olive oil', 'vegetable oil', 'coconut oil', 'canola oil',
      'sesame oil', 'vinegar'];
    if (oilKeywords.some(k => name.includes(k))) return 'Oils';

    // Seasonings keywords
    const seasoningKeywords = ['pepper', 'paprika', 'cumin', 'oregano', 'basil', 'thyme',
      'rosemary', 'cinnamon', 'ginger', 'turmeric', 'chili', 'cayenne', 'parsley', 'cilantro',
      'dill', 'sage', 'mint', 'bay leaf'];
    if (seasoningKeywords.some(k => name.includes(k))) return 'Seasonings';

    // Condiments keywords
    const condimentKeywords = ['ketchup', 'mustard', 'mayo', 'mayonnaise', 'soy sauce',
      'hot sauce', 'bbq sauce', 'worcestershire', 'ranch', 'salsa', 'relish', 'pickle'];
    if (condimentKeywords.some(k => name.includes(k))) return 'Condiments';

    // Default to empty (user can select)
    return '';
  };

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceTranscript('Listening...');
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setVoiceTranscript(transcript);
      parseVoiceInput(transcript);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      setVoiceTranscript(`Error: ${event.error}`);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      Protein: 'protein',
      Dairy: 'dairy',
      Grains: 'grain',
      Vegetables: 'leaf',
      Fruits: 'fruit',
      Baking: 'baking',
      Oils: 'oil',
      Seasonings: 'seasoning',
      Condiments: 'condiment'
    };
    return icons[category] || 'box';
  };

  const getFreshnessColor = (score: number) => {
    if (score >= 80) return 'freshness-good';
    if (score >= 50) return 'freshness-medium';
    return 'freshness-low';
  };

  const itemsByCategory = items?.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, InventoryItem[]>) || {};

  const scanning = scanReceiptMutation.isPending || scanItemMutation.isPending;

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading pantry...</p>
      </div>
    );
  }

  return (
    <div className="pantry">
      <div className="pantry-header">
        <div>
          <h1><Icon name="basket" className="icon" />Pantry</h1>
          <p>{items?.length || 0} items in your pantry</p>
        </div>
        <button onClick={openChooser} className="btn-primary">
          + Add Item
        </button>
      </div>

      {/* Hidden camera/file inputs, triggered from the chooser */}
      <input
        ref={receiptInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleReceiptFileChange}
      />
      <input
        ref={itemInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleItemFileChange}
      />

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}

      {(!items || items.length === 0) ? (
        <div className="empty-state">
          <Icon name="crate" className="icon empty-icon" />
          <h2>No Pantry Items</h2>
          <p>Add items manually or load sample data from Settings.</p>
          <button
            onClick={openChooser}
            className="btn-primary"
            style={{ marginTop: '1rem' }}
          >
            + Add Your First Item
          </button>
        </div>
      ) : (
        <div className="pantry-categories">
          {Object.entries(itemsByCategory).map(([category, categoryItems]) => (
            <div key={category} className="category-section">
              <h2 className="category-title">
                <Icon name={getCategoryIcon(category)} className="icon category-icon" />
                {category}
                <span className="category-count">({categoryItems.length})</span>
              </h2>

              <div className="items-grid">
                {categoryItems.map((item) => (
                  <div key={item.id} className="pantry-item">
                    <div className="item-header">
                      <h3>{item.name}</h3>
                      <div className="item-header-actions">
                        <span className={`freshness-badge ${getFreshnessColor(item.freshness_score)}`}>
                          {item.freshness_score}%
                        </span>
                        <button
                          className="item-edit-btn"
                          onClick={() => openEdit(item)}
                          aria-label={`Edit ${item.name}`}
                        >
                          <Icon name="edit" className="icon icon-sm" />
                        </button>
                        <button
                          className="item-delete-btn"
                          onClick={() => handleDelete(item)}
                          aria-label={`Delete ${item.name}`}
                        >
                          <Icon name="trash" className="icon icon-sm" />
                        </button>
                      </div>
                    </div>
                    <div className="item-quantity">
                      <span className="quantity-value">{item.quantity}</span>
                      <span className="quantity-unit">{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Item Chooser */}
      {showAddChooser && (
        <div className="modal-overlay" onClick={() => setShowAddChooser(false)}>
          <div className="modal-content add-chooser" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAddChooser(false)}>✕</button>
            <h2>Add to Pantry</h2>
            <div className="add-chooser-options">
              <button type="button" className="add-chooser-option" onClick={chooseReceiptScan}>
                <Icon name="receipt" className="icon add-chooser-icon" />
                <span className="add-chooser-label">Scan Receipt</span>
                <span className="add-chooser-desc">Photograph a receipt to add several items at once</span>
              </button>
              <button type="button" className="add-chooser-option" onClick={chooseItemScan}>
                <Icon name="camera" className="icon add-chooser-icon" />
                <span className="add-chooser-label">Scan Item</span>
                <span className="add-chooser-desc">Photograph a single food item to identify it</span>
              </button>
              <button type="button" className="add-chooser-option" onClick={chooseManual}>
                <Icon name="edit" className="icon add-chooser-icon" />
                <span className="add-chooser-label">Enter Manually</span>
                <span className="add-chooser-desc">Type in the details yourself</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scanning-in-progress overlay */}
      {scanning && (
        <div className="modal-overlay">
          <div className="scan-loading">
            <div className="loading-spinner" />
            <p>Analyzing photo&hellip;</p>
          </div>
        </div>
      )}

      {/* Scan results review/confirm */}
      {scanCandidates !== null && (
        <InventoryScanReview
          key={scanReviewKey}
          title={scanReviewTitle}
          items={scanCandidates}
          submitting={confirmScanMutation.isPending}
          onConfirm={(confirmedItems) => confirmScanMutation.mutate(confirmedItems)}
          onCancel={() => setScanCandidates(null)}
        />
      )}

      {/* Add Item Modal (manual entry) */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>

            <h2>Add Pantry Item</h2>

            <form onSubmit={handleSubmit} className="add-item-form">
              {/* Voice Input Section */}
              <div className="voice-input-section">
                <button
                  type="button"
                  onClick={startVoiceInput}
                  disabled={isListening}
                  className={`btn-voice ${isListening ? 'listening' : ''}`}
                >
                  <Icon name="mic" className="icon icon-sm" />
                  {isListening ? 'Listening...' : 'Voice Input'}
                </button>
                {voiceTranscript && (
                  <div className="voice-transcript">
                    <small>Heard: "{voiceTranscript}"</small>
                  </div>
                )}
                <p className="voice-hint">
                  Say something like: "Add 2 pounds of chicken breast" or "5 cups flour"
                </p>
              </div>

              <div className="form-divider">
                <span>OR ENTER MANUALLY</span>
              </div>

              {/* Manual Form Fields */}
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  placeholder="e.g., Chicken Breast"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="2"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Unit *</label>
                  <UnitSelect
                    value={newItem.unit}
                    onChange={(unit) => setNewItem({...newItem, unit})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                  >
                    <option value="">Select...</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Expiration Date</label>
                  <input
                    type="date"
                    value={newItem.expiration_date}
                    onChange={(e) => setNewItem({ ...newItem, expiration_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addItemMutation.isPending}
                  className="btn-primary"
                >
                  {addItemMutation.isPending ? 'Adding...' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setEditingItem(null)}>✕</button>

            <h2>Edit Pantry Item</h2>

            <form onSubmit={handleEditSubmit} className="add-item-form">
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm({...editForm, quantity: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Unit *</label>
                  <UnitSelect
                    value={editForm.unit}
                    onChange={(unit) => setEditForm({...editForm, unit})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  >
                    <option value="">Select...</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Expiration Date</label>
                  <input
                    type="date"
                    value={editForm.expiration_date}
                    onChange={(e) => setEditForm({ ...editForm, expiration_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateItemMutation.isPending}
                  className="btn-primary"
                >
                  {updateItemMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pantry;
